/**
 * Remediation C1 — maintain visibleToUserIds on carrier location docs so broker/shipper
 * on active in-transit loads can read locations/{carrierId} under tightened rules.
 */
import { collection, doc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { getFailoverAwareDatabase } from '../config/firebaseWithFailover';
import {
  FIRESTORE_BROKER_LOADS_COLLECTION,
  FIRESTORE_ELD_LOCATIONS_COLLECTION,
  FIRESTORE_LOADS_COLLECTION,
  FIRESTORE_LOCATION_VISIBLE_TO_FIELD,
  FIRESTORE_LOCATIONS_COLLECTION,
} from '../constants/firestoreCollectionSecurity';
import { LOAD_STATUS } from '../constants/statusValues';
import Logger from './logger';

const ACTIVE_LOAD_STATUSES = [LOAD_STATUS.ACTIVE, LOAD_STATUS.IN_PROGRESS] as const;

/**
 * Collect broker/shipper UIDs from active loads assigned to this carrier.
 */
export async function computeVisibleToUserIdsForCarrier(carrierId: string): Promise<string[]> {
  const db = getFailoverAwareDatabase();
  const visible = new Set<string>();

  for (const collectionName of [FIRESTORE_LOADS_COLLECTION, FIRESTORE_BROKER_LOADS_COLLECTION]) {
    const loadsQuery = query(
      collection(db, collectionName),
      where('carrierId', '==', carrierId),
      where('status', 'in', [...ACTIVE_LOAD_STATUSES])
    );
    const snap = await getDocs(loadsQuery);
    snap.forEach((loadDoc) => {
      const data = loadDoc.data();
      if (typeof data.brokerId === 'string' && data.brokerId) {
        visible.add(data.brokerId);
      }
      if (typeof data.shipperId === 'string' && data.shipperId) {
        visible.add(data.shipperId);
      }
      if (typeof data.userId === 'string' && data.userId) {
        visible.add(data.userId);
      }
    });
  }

  return Array.from(visible);
}

/** Merge visibleToUserIds onto locations/ and eldLocations/ for a carrier. */
export async function refreshLocationVisibilityForCarrier(carrierId: string): Promise<void> {
  if (!carrierId) return;

  const visibleToUserIds = await computeVisibleToUserIdsForCarrier(carrierId);
  const db = getFailoverAwareDatabase();
  const visibilityPatch = { [FIRESTORE_LOCATION_VISIBLE_TO_FIELD]: visibleToUserIds };

  await setDoc(doc(db, FIRESTORE_LOCATIONS_COLLECTION, carrierId), visibilityPatch, { merge: true });
  await setDoc(doc(db, FIRESTORE_ELD_LOCATIONS_COLLECTION, carrierId), visibilityPatch, { merge: true });

  Logger.debug('[locationVisibility] Refreshed visibleToUserIds', {
    carrierId,
    count: visibleToUserIds.length,
  });
}

/** Fire-and-forget refresh after load accept / complete (non-blocking for main workflow). */
export function scheduleLocationVisibilityRefresh(carrierId: string | undefined | null): void {
  if (!carrierId) return;
  void refreshLocationVisibilityForCarrier(carrierId).catch((err) => {
    Logger.warn('[locationVisibility] Refresh failed (non-critical):', err as Error);
  });
}
